import { type ToastItem, ToastVariant } from '../components/toast';
import { getConfig } from '../config';
import { AppError, mapErrCoded } from '../error';
import { convertError } from '../error/convert';
import { getQueryClient } from '../sdk/request';
import { getAuthUserFromCache } from '../services/storage/users-queries';
import { tGlobal } from '../services/translation';
import { getSelectedBrand } from '../shared/brand';
import { getSelectedOrganization } from '../shared/organization';

const errCache: unknown[] = [];

export function captureException(
  err: any,
  info: {
    file: string;
    method: string;
    infoMessage: string;
    [key: string]: any;
  },
  trackError: (err: any, info?: { [key: string]: any }) => unknown,
  // TODO: this should be a service!
  addToast?: (toast: ToastItem) => unknown
) {
  // inform the user that something has happened
  if (addToast != null) {
    addToast({
      title: tGlobal('somethingWentWrong'),
      variant: ToastVariant.Error,
      id: Date.now(),
    });
  }

  const parsedErr = err instanceof AppError ? err : convertError(err, info);
  const parsedInfo = {
    ...info,
    ...parsedErr.info,
  };

  // track the error to our error central
  const trackMsg = parsedInfo?.infoMessage ?? parsedErr?.cause ?? parsedErr?.message;
  const ErrClass = mapErrCoded(parsedErr.code);
  const trackedError =
    ErrClass == null
      ? parsedErr
      : new ErrClass(trackMsg, {
          info: parsedInfo,
          cause: parsedErr,
        });

  if (getConfig().env === 'development' || getConfig().env === 'staging') {
    // eslint-disable-next-line no-console
    console.error(parsedErr, err);
    errCache.push({ parsedErr, err: err as unknown });
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    (window as any).errCache = errCache;
    return;
  }

  // setup the tracking for sentry
  Promise.resolve()
    .then(function () {
      // Use TanStack Query cache to get auth user (synchronous, no network call)
      // This prevents potential infinite loops when tracking errors
      const queryClient = getQueryClient();
      const authUser = getAuthUserFromCache(queryClient);
      const org = getSelectedOrganization(authUser) ?? undefined;
      const brand = getSelectedBrand(authUser) ?? undefined;

      return { userId: authUser?._id, organizationId: org, brandId: brand };
    })
    .then(function (data) {
      trackError(trackedError, {
        extra: {
          ...data,
          cause: parsedErr.cause,
          message: parsedErr.message,
          ...parsedInfo,
        },
        tags: {
          ...data,
          code: parsedErr.code,
        },
      });
    })
    .catch(function (err) {
      // track the error where the tracking failed
      const parsedErr = err instanceof AppError ? err : convertError(err, parsedInfo);
      trackError(trackedError, {
        extra: {
          cause: JSON.stringify(parsedErr.cause ?? {}, null, 2),
          message: parsedErr.message,
          ...parsedInfo,
        },
        tags: {
          code: parsedErr.code,
        },
      });
    });
}
